import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useHistory } from 'react-router-dom';
import { getLinkInfo } from '~/common/utils';
import { SERVER_URL } from '~/env';
import { axios } from '~/root';
import { qk } from '~/root/query-keys';
import { SignupRequest, LoginRequest, loginSchema, signupSchema, CustomerUser } from '../domain';
import { authorizeUser, reportReturningWhenOrdering } from '../utils';
import { useSetUser } from './init';
import { useOauthLogin } from './oauth';

export const useLogin = () => {
  const { mutateAsync: oauthLogin } = useOauthLogin();
  const setUser = useSetUser();
  const client = useQueryClient();

  return useMutation({
    mutationKey: qk.login,
    mutationFn: (data: LoginRequest) =>
      axios
        .post('/v1/users/login', data)
        .then((res) => {
          const user = loginSchema.parse(res.data);

          // Just TS fix, user can be staff too)
          setUser(user as CustomerUser);
          authorizeUser(user);
          reportReturningWhenOrdering();
          client.invalidateQueries(qk.init);

          return user;
        })
        .catch(async (error) => {
          // TODO: Check is it checked on axios interceptors level and probably can be removed
          const redirectUrl = error.response?.data?.redirectUrl;

          if (error instanceof AxiosError && error.response?.status === 412 && redirectUrl) {
            window.location.href = redirectUrl;
            return error;
          }

          if (redirectUrl && error.response?.data.channel) {
            await oauthLogin({
              provider: error.response.data.channel,
              oauthData: error.response?.data,
              action: 'login',
            });
          }

          return Promise.reject(error);
        }),
  });
};

const ORDER_FORM_NAMES = ['order', 'try-us-out', '10-for-49'];

export const useSignup = () => {
  const history = useHistory();

  return useMutation({
    mutationKey: qk.signup,
    mutationFn: (payload: SignupRequest) =>
      axios.post('/v1/users/register', payload).then((res) => signupSchema.parse(res.data)),
    onSuccess: (data) => {
      // TODO staging-only simulation of validating email
      // for signups inside order form we'll open a new tab to avoid losing
      // form progress, since there are dummy files which weren't uploaded yet
      if (data.token && ORDER_FORM_NAMES.some((name) => window.location.pathname.includes(name))) {
        window.open(`${SERVER_URL}/v1/users/verify/${data.token}`);
      }

      // for signups outside order form we'll replace the url for tests to work
      if (
        data.token &&
        ORDER_FORM_NAMES.every((name) => !window.location.pathname.includes(name))
      ) {
        window.location.href = `${SERVER_URL}/v1/users/verify/${data.token}`;
      }

      // redirecting staff users to the Inner Platform
      if (!window.location.pathname.includes('step/') && !data.token && data.redirectUrl) {
        const { client, url } = getLinkInfo(data.redirectUrl);
        if (client) {
          history.push(url);
        } else {
          window.location.href = url;
        }
      }
    },
  });
};
